// src/components/ErrorBoundary.jsx
import { Component } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import Button from './ui/Button'

class ErrorBoundary extends Component {
    constructor(props) {
        super(props)
        this.state = { hasError: false, error: null, errorInfo: null }
    }

    static getDerivedStateFromError(error) {
        return { hasError: true }
    }

    componentDidCatch(error, errorInfo) {
        this.setState({
            error,
            errorInfo
        })

        // Log error to console in development
        if (import.meta.env.DEV) {
            console.error('Error Boundary caught an error:', error, errorInfo)
        }
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null, errorInfo: null })
    }

    handleGoHome = () => {
        window.location.href = '/'
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
                    <div className="max-w-md w-full text-center">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
                            <div className="text-red-500 mb-4">
                                <AlertTriangle className="w-16 h-16 mx-auto" />
                            </div>

                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                Oops! Something went wrong
                            </h1>

                            <p className="text-gray-600 dark:text-gray-300 mb-6">
                                We're sorry, but something unexpected happened. Please try refreshing the page or go back to the home page.
                            </p>

                            <div className="space-y-3">
                                <Button
                                    onClick={this.handleRetry}
                                    variant="primary"
                                    icon={RefreshCw}
                                    className="w-full"
                                >
                                    Try Again
                                </Button>

                                <Button
                                    onClick={this.handleGoHome}
                                    variant="secondary"
                                    icon={Home}
                                    className="w-full"
                                >
                                    Go Home
                                </Button>
                            </div>

                            {import.meta.env.DEV && this.state.error && (
                                <details className="mt-6 text-left">
                                    <summary className="text-sm text-gray-500 cursor-pointer">
                                        Error Details (Development)
                                    </summary>
                                    <pre className="mt-2 text-xs bg-gray-100 dark:bg-gray-700 p-2 rounded overflow-auto">
                    {this.state.error.toString()}
                                        {this.state.errorInfo.componentStack}
                  </pre>
                                </details>
                            )}
                        </div>
                    </div>
                </div>
            )
        }

        return this.props.children
    }
}

export default ErrorBoundary